import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { CompanyBasic, TextContent } from '../../shared/models';
import { CRMAlarm} from '../../shared/models/crm/CRMAlarm.model';
import { AbstractCRUD, ExceptionService, PaginatedList, SanitizeService, UtilsService } from '../../xform-compat';
import { CrmAlarmUtilsService } from './crm-alarm-utils.service';
import { CrmUserService } from './crm-user.service';
import { BaseCRMItem, CRMItem, WatchedItem } from '../../shared/models/crm/CRMItem.model';
import { DateTimeService, ContentService } from '../services';
import { CONFIG } from '../../config';
import { CRMItemHistory } from '../../shared/models/crm/CRMItemHistory.model';
import { CrmStatusService } from './crm-status.service';
import { Domain } from '../../shared/models/domain.model';
import { BaseCRMServiceInt } from './base-crm-service.interface';
import { CrmUtilsService } from './crm-utils.service';


@Injectable({
  providedIn: 'root'
})
export class CrmService extends AbstractCRUD<BaseCRMItem> implements BaseCRMServiceInt {

  public readonly NEW_ITEM_NOTE_CONTENT = 'A new Sonar record has been created and is now active';

  private crmUserService: any;
  private crmAlarmUtilsService: any;
  private sanitizeService: any;
  private crmStatusService: any;

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    crmUserService: CrmUserService,
    crmAlarmUtilsService: CrmAlarmUtilsService,
    sanitizeService: SanitizeService,
    crmStatusService: CrmStatusService,
    private crmUtilsService: CrmUtilsService,
    private contentService: ContentService,
  ) {
    super(exceptionService, http, utilsService);
    this.crmUserService = crmUserService;
    this.crmAlarmUtilsService = crmAlarmUtilsService;
    this.sanitizeService = sanitizeService;
    this.crmStatusService = crmStatusService;
  }

  public getResourcePath(): string {
    return '/crm';
  }

  public getAllCrmItemsAdmin(filter?: any): Observable<any> {
    const params = this.crmUtilsService.setRequestParams(filter);
    return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/all/admin`, filter), {params: params})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getAllCrmItemsSilenced(filter?: any): Observable<any> {
    let params = this.crmUtilsService.setRequestParams(filter);
    params = params.append('includeSilenced', String(true));
    return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/all/silenced`, filter), {params: params})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getAllCrmItemsProjects(filter?: any): Observable<any> {
    const params = this.crmUtilsService.setRequestParams(filter);
    return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/all/projects`, filter), {params: params})
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getUserDashboardCrmItems(filter?: any): Observable<any> {
    return this.crmUserService.getDomainsById(filter.userId).pipe(mergeMap((dmns: Array<Domain>) => {
      filter.userDomainId = dmns.map(d => d.id);
      let params = this.crmUtilsService.setRequestParams(filter);
      if (!filter.excludeTerminalStatusCutoff) {
        params = params.append('terminalStatusCutoff', this.crmUtilsService.getDateForTerminalCutoff());
      }
      return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/all/userDashboard`, filter), {params: params})
        .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
    }));
  }

  public getAllCrmItems(filter?: any): Observable<any> {
    return this.crmUserService.getDomainsById(filter.userId).pipe(mergeMap((dmns: Array<Domain>) => {
      filter.userDomainId = dmns.map(d => d.id);
      let params = this.crmUtilsService.setRequestParams(filter);
      if (!filter.excludeTerminalStatusCutoff) {
        params = params.append('terminalStatusCutoff', this.crmUtilsService.getDateForTerminalCutoff());
      }
      return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/all`, filter), {params: params})
       .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
    }));
  }

  public getDomainCrmItems(filter?: any): Observable<any> {
    return this.crmUserService.getDomainsById(filter.userId).pipe(mergeMap((dmns: Array<Domain>) => {
      filter.userDomainId = dmns.map(d => d.id);
      let params = this.crmUtilsService.setRequestParams(filter);
      if (!filter.excludeTerminalStatusCutoff) {
        params = params.append('terminalStatusCutoff', this.crmUtilsService.getDateForTerminalCutoff());
      }
      return super.getHTTP().get(this.handleOptions(`${this.getApiPath()}/item/domain`, filter), {params: params})
       .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
    }));
  }

  public getItem(itemId: number): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/item/${itemId}`)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getRecords(filter?: any): Observable<CRMItem> {
    return this.getAllCrmItems(filter);
  }

  public getRecord(itemId: number): Observable<CRMItem> {
    return this.getItem(itemId);
  }

  public validateCrmItem(crmItem: any) {
    return super.getHTTP().put(`${this.getApiPath()}/item/validate`,
      { ...crmItem }).pipe(catchError(ex =>
      super.getException().catchBadResponse(ex)));
  }

  public createCRM(crmData: any): Observable<any> {
    const note: any = {
      content: this.sanitizeService.sanitizeNoteContent(this.NEW_ITEM_NOTE_CONTENT),
      isOverview: false
    };
    crmData = this.reformatCrmData(crmData);
    return this.createCRMItem(crmData)
      .pipe(mergeMap(crm => this.handleSilencedStatus(crm.id, crm, crmData.silenceUntil)))
      .pipe(mergeMap(crm => this.createNote(crm.id, note).pipe(() => of(crm))));
  }

  public updateCRM(crmId: number, crmData: any): Observable<any> {
    if (crmData.companies && crmData.companies.length > 0 && crmData.companies[0].storm === null &&
      (crmData.companies[0].company && !crmData.companies[0].company.isPrivate)) {
      crmData.companies[0].storm = 'PUBLIC';
    }
    crmData = this.reformatCrmData(crmData);
    return this.updateCRMItem(crmId, crmData).pipe(
      mergeMap(crm => this.handleSilencedStatus(crmId, crm, crmData.silenceUntil).pipe(map((alarm) =>  {
        if (alarm) {
          crm.alarms = [alarm];
          return crm;
        }
        return crm;
      })))
    );
  }

  public addOwner(itemId: number, userId: number): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/item/${itemId}/owner/${userId}`,
      {}).pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public removeOwner(itemId: number, userId: number): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/item/${itemId}/owner/${userId}/remove`,
      {}).pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public createNote(crmId: number, note: any): Observable<any> {
    note.content = this.contentService.formatInlineImageContentRemove(note.content);
    return super.getHTTP().post(`${this.getApiPath()}/item/${crmId}/notes`, note)
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getCrmItemHistory(crmId: number): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/item/${crmId}/audit`)
    .pipe(map((response: Array<CRMItemHistory>) => response.map(r => ({
      ...r,
      eventTimestamp: DateTimeService.convertToEst(`${r.eventTimestamp}`,
        CONFIG.formatEST, CONFIG.dateTimeServerFormat)
    }))))
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public addAlarm(crmId: number, alarm: CRMAlarm): Observable<any> {
    return super.getHTTP().post(`${this.getApiPath()}/alarm/crmitem/${crmId}`, alarm)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public removeAlarm(crmAlarmId: number): Observable<any> {
    return super.getHTTP().delete(`${this.getApiPath()}/alarm/${crmAlarmId}`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getWatchedItems({page, pageSize, domainId}:
      { page?: number, pageSize?: number, domainId?: Array<string> }): Observable<PaginatedList<WatchedItem>> {
    let params: HttpParams = new HttpParams();
    if (page !== null) {
      params = params.set('page', String(page));
    }
    if (pageSize !== null) {
      params = params.set('pagesize', String(pageSize));
    }
    if (domainId !== null && domainId.length > 0) {
      domainId.forEach(domain => params = params.append('domainId', domain));
    }
    return super.getHTTP().get(`${this.getApiPath()}/watched/items`, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public getCompanyNotes(companyId: number): Observable<Array<TextContent>> {
    const params = new HttpParams().set('companyId', String(companyId));
    return super.getHTTP().get(`${this.getApiPath()}/watched/companynotes`, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public postCompanyNote(companyId: number, note: any): Observable<any> {
    note.content = this.contentService.formatInlineImageContentRemove(note.content);
    const params = new HttpParams().set('companyId', String(companyId));
    return super.getHTTP().post(`${this.getApiPath()}/watched/companynote`, note, { params: params })
      .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public searchCompanies (params?: any): Observable<Array<CompanyBasic>> {
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return super.getHTTP()
      .get(`${this.getApiPath()}/item/autocomplete/open/companies`, {params: httpParams}).pipe(
      catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public updateRecord(record: any): Observable<any> {
    return this.updateCRM(record.id, record);
  }

  public createRecord(record: any): Observable<any> {
    return this.createCRM(record);
  }

  private handleSilencedStatus(crmId: number, crmData: any, silenceUntil: any): Observable<CRMItem> {
    if (this.crmStatusService.isSilenceStatus(crmData.status) && silenceUntil) {
      return concat(
        this.removeSilenceAlarm(crmData),
        this.addAlarm(crmId, this.crmAlarmUtilsService.createSilenceAlarm(crmId, silenceUntil, crmData.status))
      );
    }
    const item: CRMItem = crmData;
    item.id = crmId;
    return of(item);
  }


  public removeSilenceAlarm(crmData: any): Observable<CRMItem> {
    const currentAlarm = this.crmAlarmUtilsService.findSilenceAlarm(crmData);
    if (currentAlarm) {
      return this.removeAlarm(currentAlarm.id);
    }
    return of(crmData);
  }

  private createCRMItem(crmData: any): Observable<any> {
    return super.getHTTP().post(`${this.getApiPath()}/item/create`,
      { ...crmData }).pipe(catchError(ex =>
      super.getException().catchBadResponse(ex)));
  }

  private updateCRMItem(crmId: number, crmData: any): Observable<any> {
    return super.getHTTP().put(`${this.getApiPath()}/item/${crmId}/update`,
      { ...crmData }).pipe(catchError(ex =>
      super.getException().catchBadResponse(ex)));
  }

  private reformatCrmData(crmData) {
    return {
      ...crmData,
      companies: crmData.companies ? crmData.companies.map(c =>
        c.courtshipType ? c :
        {
          ...c,
          courtshipType: c.company.courtshipType
        }
      ) : []
    };
  }

  private handleOptions(url: string, params: any): string {
    return params.filterOptions ? `${url}/options` : url;
  }
}
